import React, { ReactNode } from 'react';

// Components
import { useTranslation } from 'react-i18next';
import AbstractFormsComboBox, { ComboBoxProps } from '../AbstractComboBox/AbstractFormsComboBox';
import { TermSuggestResponse } from '../AbstractComboBox/api/TermSuggestResponse';
import useComboBoxInput, { ComboBoxInputProps } from '../AbstractComboBox/hooks/useComboBoxInput';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import { Selected } from '../AbstractComboBox/hooks/usePopupKeyboardNavigation';
import { TopKeywordResult } from '../AbstractComboBox/types/SuggestResultTypes';
import { usePopup } from '../AbstractComboBox/hooks/usePopup';
import ListItemEmpty from '../AbstractComboBox/ListItemEmpty/ListItemEmpty';
import { Section } from '../AbstractComboBox/types/Section';
import { Namespace } from '../../../../../../translation/namespace';
import ListItemKeyword from '../AbstractComboBox/ListItemKeyword/ListItemKeyword';

type TermSuggestComboBoxProps = ComboBoxProps &
  ComboBoxInputProps & { onSelection: (item?: TopKeywordResult) => void; selectedItems: TopKeywordResult[] };

/**
 * returns true if the input value is not empty and not already selected or suggested.
 */
function isNewNonEmptyInputValue(
  inputValue: string | undefined,
  suggestedResults: IEntity[],
  selectedItems: IEntity[],
): boolean {
  return (inputValue && selectedItems.concat(suggestedResults).every(s => s.id !== inputValue)) || false;
}

const transformSuggestResponse = (responseData: TermSuggestResponse): Section<TopKeywordResult> => {
  const suggestedResults: TopKeywordResult[] = responseData.terms.map(({ term }) => ({
    id: term,
    title: term,
    type: 'topKeywordResult',
  }));

  return {
    key: 'terms',
    results: suggestedResults,
  };
};

export default function TermSuggestComboBox(props: TermSuggestComboBoxProps) {
  const popupProps = usePopup();
  const inputProps = useComboBoxInput<TermSuggestResponse>(props);
  const { t } = useTranslation(Namespace.Keywords);
  const { response, inputValue, isValidInputValue, formInputRef } = inputProps;

  const termSection = response && transformSuggestResponse(response);

  if (
    termSection &&
    inputValue &&
    isValidInputValue &&
    isNewNonEmptyInputValue(inputValue, termSection.results, props.selectedItems)
  ) {
    termSection.results.push(TopKeywordResult(inputValue, `${inputValue} (${t('form.newKeyword')})`));
  }

  const renderListItem = (item: TopKeywordResult): ReactNode => <ListItemKeyword title={item.title} />;

  const onSelection = ({ event, selectedItem }: Selected<TopKeywordResult>) => {
    event.preventDefault();

    if (selectedItem) {
      // create TopKeywordResult from raw input
      const isRawInput = typeof selectedItem === 'string';
      const newItem = isRawInput ? TopKeywordResult(selectedItem.trim()) : TopKeywordResult(selectedItem.id);
      props.onSelection(newItem);
      popupProps.closeResults();
      formInputRef.current?.select();
    }
  };

  return (
    <AbstractFormsComboBox
      {...props}
      {...inputProps}
      {...popupProps}
      results={termSection && [termSection]}
      onSelection={onSelection}
    >
      {item => (item ? renderListItem(item) : <ListItemEmpty displayText={`${Namespace.Keywords}:form.alreadyUsed`} />)}
    </AbstractFormsComboBox>
  );
}
